/**
 * OpenSpec 021 — cliente de la unificación de contactos.
 *
 * Semántica fija: **destino** (el contacto que se queda, va en la URL) y **origen**
 * (`sourceContactId`, el que se absorbe y desaparece).
 *
 * La ejecución es **irreversible**: el customer de Magento del origen se elimina. Por eso el flujo
 * obligado es candidatos → preview → confirmación explícita → merge.
 */
import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";

export type MergeChoice = "target" | "source";

export interface MergeCandidate {
  contactId: number;
  userId: number;
  name: string;
  email: string;
  /** El email es un placeholder `<numero>@wppxcons.com` del alta por WhatsApp. */
  isSyntheticEmail: boolean;
  whatsapp?: string | null;
  stores: string[];
  /**
   * D1′: `false` cuando el candidato no tiene ficha en el store desde el que se unifica.
   * La unificación es de identidad y sigue siendo válida, pero el operador tiene que saberlo.
   */
  inTargetStore: boolean;
  quotesCount: number;
  ordersCount: number;
  /** phone | email | name | manual — por qué apareció en la lista. */
  matchReason: "phone" | "email" | "name" | "manual";
}

export interface MergeFieldConflict {
  field: string;
  targetValue: string | null;
  sourceValue: string | null;
  default: MergeChoice;
  reason: string;
  /** Sólo estos son una decisión real del operador; el resto es informativo. */
  isConflict: boolean;
}

export interface MergeBlocker {
  code: string;
  message: string;
}

export interface MergeWarning {
  code: string;
  message: string;
  count?: number;
}

export interface MergePreview {
  targetContactId: number;
  sourceContactId: number;
  /**
   * Sólo los stores que **el operador administra**. El backend no manda los nombres de los ajenos:
   * al operador de un store no le corresponde ver el padrón de stores donde una persona tiene
   * ficha (O5′).
   */
  storesAffected: string[];
  /** Cuántos stores afectados quedaron sin nombrar por no ser del operador. */
  storesAffectedHidden?: number;
  counts: Record<string, number>;
  fieldConflicts: MergeFieldConflict[];
  warnings: MergeWarning[];
  blockers: MergeBlocker[];
}

export interface MergeResult {
  mergeId: number;
  status:
    | "pending"
    | "in_progress"
    | "verifying"
    | "deleting"
    | "done"
    | "failed";
  step: string | null;
  counts?: Record<string, number> | null;
  error?: string | null;
  targetContactId: number;
  sourceContactId: number;
  requestedAt: string;
  completedAt?: string | null;
}

export type MergeFieldChoices = Partial<Record<string, MergeChoice>>;

export interface MergeStore {
  storeCode: string;
  contactId: number;
  archived: boolean;
}

/**
 * Stores donde esta identidad tiene ficha (y el operador es admin de Contactos).
 *
 * La unificación es **dentro de un mismo store**, y el store lo determina la ficha destino: esto
 * permite mostrarlo y dejar cambiarlo sin navegar a la otra ficha.
 */
export async function fetchMergeStores(
  contactId: number,
): Promise<MergeStore[]> {
  const { data } = await axiosInstance.get(
    `${API_BASE_URL}/contacts/${contactId}/merge-stores`,
  );
  return data ?? [];
}

export async function fetchMergeCandidates(
  targetContactId: number,
  q?: string,
): Promise<MergeCandidate[]> {
  const { data } = await axiosInstance.get(
    `${API_BASE_URL}/contacts/${targetContactId}/merge-candidates`,
    { params: q ? { q } : undefined },
  );
  return data ?? [];
}

export async function fetchMergePreview(
  targetContactId: number,
  sourceContactId: number,
  fieldChoices?: MergeFieldChoices,
): Promise<MergePreview> {
  const { data } = await axiosInstance.post(
    `${API_BASE_URL}/contacts/${targetContactId}/merge/preview`,
    { sourceContactId, fieldChoices },
  );
  return data;
}

/**
 * Ejecuta la unificación.
 *
 * La `Idempotency-Key` la genera el front y **se mantiene entre reintentos del mismo intento**: la
 * ejecución toca 4 almacenes sin transacción distribuida, y sin la clave un reintento tras un
 * timeout de red arrancaría de cero sobre datos ya movidos.
 */
export async function executeMerge(
  targetContactId: number,
  sourceContactId: number,
  idempotencyKey: string,
  fieldChoices?: MergeFieldChoices,
): Promise<MergeResult> {
  const { data } = await axiosInstance.post(
    `${API_BASE_URL}/contacts/${targetContactId}/merge`,
    { sourceContactId, confirm: true, fieldChoices },
    { headers: { "Idempotency-Key": idempotencyKey } },
  );
  return data;
}

export async function fetchContactMerges(
  contactId: number,
): Promise<MergeResult[]> {
  const { data } = await axiosInstance.get(
    `${API_BASE_URL}/contacts/${contactId}/merges`,
  );
  return data ?? [];
}

/** Etiquetas de las entidades que se trasladan, para el resumen del preview. */
export const MERGE_COUNT_LABELS: Record<string, string> = {
  quotes: "Presupuestos",
  orders: "Pedidos",
  ordersGrid: "Pedidos (grid)",
  shipments: "Entregas",
  addresses: "Direcciones",
  adminAddresses: "Direcciones (admin)",
  groups: "Grupos",
  relations: "Relaciones",
  relationsReverse: "Relaciones inversas",
  participations: "Participaciones",
  contactSellers: "Sellers asignados",
  vendorLinks: "Vínculos con vendors",
  wishlists: "Favoritos",
  rewardHistory: "Historial de puntos",
  rewardBalance: "Saldo de puntos",
  newsletter: "Newsletter",
  reviews: "Reseñas",
  priceAdjustments: "Ajustes de precio",
  historyLegacy: "Historial",
  conversations: "Conversaciones",
  customerLog: "Registro de accesos",
};

/** Motivo del match, en palabras. */
export const MATCH_REASON_LABELS: Record<string, string> = {
  phone: "Mismo teléfono",
  email: "Mismo email",
  name: "Nombre parecido",
  manual: "Búsqueda manual",
};
